import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Method, Unavailable } from "./Bits";

const DIMS = [["academic", "Research"], ["outcomes", "Outcomes"], ["teaching", "Teaching"]];

const Pct = ({ v }) => <span className="font-mono">{v == null ? "—" : Math.round(v)}</span>;

function Ranked({ rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
      <table className="w-full min-w-[640px] text-sm" data-testid="sci-proto-table">
        <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wider text-stone-500">
          <tr><th className="px-3 py-2">90% rank range</th><th className="px-3 py-2">University</th><th className="px-3 py-2 text-right">Score</th>
            {DIMS.map(([k, l]) => <th key={k} className="px-3 py-2 text-right">{l} pct</th>)}<th className="px-3 py-2 text-right">REF GPA</th></tr>
        </thead>
        <tbody>{rows.map((r) => (
          <tr key={r.university_id} data-testid={`sci-proto-row-${r.university_id}`} className="border-t border-stone-100">
            <td className="px-3 py-2 font-mono text-stone-700">{r.rank_interval_90[0]}–{r.rank_interval_90[1]}</td>
            <td className="px-3 py-2"><span className="text-stone-900">{r.university}</span><span className="block text-[11px] text-stone-500">{r.course}</span></td>
            <td className="px-3 py-2 text-right font-mono">{r.score}</td>
            {DIMS.map(([k]) => <td key={k} className="px-3 py-2 text-right"><Pct v={r.dimension_pct[k]} /></td>)}
            <td className="px-3 py-2 text-right font-mono">{r.inputs.ref_gpa?.value}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export function RankingPrototype() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/sci/preview/rankings/philosophy").then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  if (!d) return null;
  if (d.error) return <Unavailable testId="sci-proto-error">The research prototype couldn't load.</Unavailable>;
  const s = d.sensitivity;
  return (
    <details className="rounded-2xl border-2 border-dashed border-violet-300 bg-violet-50/40 p-5" data-testid="sci-proto">
      <summary className="cursor-pointer text-sm font-semibold text-violet-900">Admin research prototype: WUA Philosophy ranking {d.version} (not approved, not for students)</summary>
      <div className="mt-4 space-y-5">
        <ul className="space-y-1.5" data-testid="sci-proto-blockers">{d.blockers.map((b, i) => (
          <li key={i} className="flex gap-2 text-xs text-amber-900"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{b}</li>))}</ul>
        <p className="text-sm text-stone-700" data-testid="sci-proto-counts">{d.counts.ranked} of {d.counts.pool_universities} universities have enough evidence to be placed. Overlapping ranges mean the universities can't be told apart. Provisional weights: research {d.weights_provisional.academic}, outcomes {d.weights_provisional.outcomes}, teaching {d.weights_provisional.teaching} (renormalised).</p>
        <Ranked rows={d.ranked} />
        <div className="grid gap-4 text-xs text-stone-700 md:grid-cols-2">
          <div data-testid="sci-proto-sensitivity"><p className="mb-1 font-semibold text-stone-900">Sensitivity (how much the order changes when weights move)</p>
            {Object.entries(s).map(([k, v]) => <p key={k}>{k.replace(/_/g, " ")}: rank correlation {v.spearman_vs_base}, {v.top10_overlap}/10 of the top 10 kept</p>)}</div>
          <div data-testid="sci-proto-bias"><p className="mb-1 font-semibold text-stone-900">Bias checks</p>
            <p>Score vs entry tariff: rank correlation {d.bias_checks.spearman_score_vs_entry_tariff}</p>
            <p>Score vs REF department size (FTE): rank correlation {d.bias_checks.spearman_score_vs_ref_fte_size}</p>
            <p>London universities: {d.bias_checks.london_share_top10} in the top 10, {d.bias_checks.london_share_pool} in the pool</p></div>
        </div>
        <details data-testid="sci-proto-unranked"><summary className="cursor-pointer text-xs font-semibold text-stone-900">Not placed: insufficient evidence ({d.unranked.length})</summary>
          <ul className="mt-2 space-y-1 text-xs text-stone-600">{d.unranked.map((r) => <li key={r.university_id}><span className="text-stone-900">{r.university}</span>: {r.exclusions.join("; ") || "missing inputs"}</li>)}</ul></details>
        <Method testId="sci-proto-method">{d.method.normalisation}. {d.method.dimension}. {d.method.eligibility}. {d.method.uncertainty}. Sources: {d.sources.ref2021.name} ({d.sources.ref2021.licence}); {d.sources.discover_uni.name} ({d.sources.discover_uni.licence}). Seed {d.seed}, reproduce with scripts/sci_wua_ranking_philosophy.py.</Method>
      </div>
    </details>
  );
}
